import { useState } from 'react'
import PropTypes from 'prop-types'
import { FieldArray } from 'formik'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { Trash, Plus, DotsSixVertical } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import TextField from '../FormLegacy/FormFieldsV2/TextField'
import { generateId } from '../../utils'

import { satisfactionSurvey, inAppCommunication } from './helpers'

const SortableAnswerOption = ({
  id,
  option,
  optionIndex,
  questionIndex,
  onRemove,
  handleChange,
  isEditingDisabled,
}) => {
  const { t } = useTranslation(['surveys'])
  const { ref } = useSortable({ id, index: optionIndex, disabled: isEditingDisabled })
  return (
    <div ref={ref} className="drag-and-drop__item">
      <div className="drag-and-drop__header">
        <DotsSixVertical size={20} />
      </div>
      <div className="drag-and-drop__body">
        <TextField
          name={`questions[${questionIndex}].answerOptions[${optionIndex}]`}
          maxLength={50}
          placeholder={t('surveys:form.addAnswerPlaceholder')}
          value={option}
          onChange={handleChange}
          additionalClass="survey-form__input"
          extraFieldClass="survey-form__field"
          disabled={isEditingDisabled}
        />
        {!isEditingDisabled && optionIndex > 0 ? (
          <div
            className="survey-form__button survey-form__question--delete-btn transparent"
            onClick={() => onRemove(optionIndex)}
          >
            <Trash size={20} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

const AnswerOptionsList = ({
  questionIndex,
  answerOptions,
  arrayHelpers,
  handleChange,
  isEditingDisabled,
}) => {
  const { t } = useTranslation(['surveys'])
  const [optionIds, setOptionIds] = useState(() => answerOptions.map(() => generateId()))

  const onDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return

    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return

    arrayHelpers.move(sourceIndex, targetIndex)
    setOptionIds((ids) => {
      const newIds = [...ids]
      const [moved] = newIds.splice(sourceIndex, 1)
      newIds.splice(targetIndex, 0, moved)
      return newIds
    })
  }

  const handleAdd = () => {
    arrayHelpers.push('')
    setOptionIds((ids) => [...ids, generateId()])
  }

  const handleRemove = (index) => {
    arrayHelpers.remove(index)
    setOptionIds((ids) => ids.filter((_, i) => i !== index))
  }

  return (
    <div className="drag-and-drop__wrap">
      <DragDropProvider onDragEnd={onDragEnd}>
        <div className="drag-and-drop">
          {answerOptions.map((option, optionIndex) => (
            <SortableAnswerOption
              key={optionIds[optionIndex]}
              id={optionIds[optionIndex]}
              option={option}
              optionIndex={optionIndex}
              questionIndex={questionIndex}
              onRemove={handleRemove}
              handleChange={handleChange}
              isEditingDisabled={isEditingDisabled}
            />
          ))}
        </div>
      </DragDropProvider>
      {!isEditingDisabled && answerOptions.length < 6 && (
        <button type="button" className="survey-form__button transparent" onClick={handleAdd}>
          <Plus size={20} />
          {t('surveys:form.addAnotherField')}
        </button>
      )}
    </div>
  )
}

const SurveyQuestions = ({ values, handleChange, isEditingDisabled }) => {
  const { t } = useTranslation(['surveys'])
  return (
    <div className="survey-form__question--wrap">
      <FieldArray
        name="questions"
        render={(arrayHelpers) => {
          const isAddQuestionButtonDisabled =
            values.communicationType !== inAppCommunication || values.questions.length === 5
          return values.questions.map(({ title, answerOptions }, questionIndex) => (
            <div key={questionIndex} className="subtitle__wrap">
              {questionIndex === 0 || values.type !== satisfactionSurvey ? (
                <div className="survey-form__text">{t('surveys:form.questionLabel')}</div>
              ) : null}
              <div className="survey-form__question">
                <div className="survey-form__question--item">
                  <TextField
                    maxLength={255}
                    name={`questions[${questionIndex}].title`}
                    placeholder={t('surveys:form.enterSurveyQuestion')}
                    value={title || ''}
                    onChange={handleChange}
                    additionalClass="survey-form__input"
                    extraFieldClass="survey-form__field"
                    disabled={isEditingDisabled}
                  />
                  {!isEditingDisabled && questionIndex > 0 ? (
                    <div
                      className="survey-form__button survey-form__question--delete-btn"
                      onClick={() => arrayHelpers.remove(questionIndex)}
                    >
                      <Trash size={20} />
                    </div>
                  ) : null}
                </div>
                {values.type !== satisfactionSurvey && (
                  <FieldArray
                    name={`questions[${questionIndex}].answerOptions`}
                    render={(arrayHelpers) => (
                      <AnswerOptionsList
                        questionIndex={questionIndex}
                        answerOptions={answerOptions}
                        arrayHelpers={arrayHelpers}
                        handleChange={handleChange}
                        isEditingDisabled={isEditingDisabled}
                      />
                    )}
                  />
                )}
                {!isEditingDisabled && questionIndex === values.questions.length - 1 ? (
                  <button
                    type="button"
                    className={`survey-form__button ${
                      isAddQuestionButtonDisabled ? 'disabled' : ''
                    }`}
                    onClick={() => arrayHelpers.push({ title: '', answerOptions: [''] })}
                    disabled={isAddQuestionButtonDisabled}
                  >
                    <Plus size={20} />
                    {t('surveys:form.addQuestion')}
                  </button>
                ) : null}
              </div>
            </div>
          ))
        }}
      />
    </div>
  )
}

export default SurveyQuestions

SurveyQuestions.propTypes = {
  values: PropTypes.object,
  handleChange: PropTypes.func,
  isEditingDisabled: PropTypes.bool,
}
